import { useState } from 'react'
import {
  Card,
  Table,
  Input,
  Button,
  Modal,
  Message,
  Empty,
} from '@arco-design/web-react'
import { IconRefresh, IconSearch } from '@arco-design/web-react/icon'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import PageHeader from '@/components/Layout/PageHeader'
import { Pill, renderTimestamp } from '@/utils/render'
import { renderQuota, displayAmountToQuota } from '@/utils/quota'
import {
  getAllTopupsApi,
  adminCompleteTopupApi,
  type TopUpRecord,
} from '@/api/topup'
import { getMethodLabel } from '@/components/topup/PaymentMethodPicker'

const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'danger' | 'neutral'> = {
  success: 'success',
  pending: 'warning',
  failed: 'danger',
  expired: 'danger',
}

const STATUS_TEXT: Record<string, string> = {
  success: '成功',
  pending: '待支付',
  failed: '失败',
  expired: '已过期',
}

export default function TopUpOrdersPage() {
  const { t } = useTranslation()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [keyword, setKeyword] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const qc = useQueryClient()

  const list = useQuery({
    queryKey: ['admin-topups', { page, pageSize, keyword }],
    queryFn: () => getAllTopupsApi({ p: page, page_size: pageSize, keyword: keyword || undefined }),
  })

  const completeMut = useMutation({
    mutationFn: adminCompleteTopupApi,
    onSuccess: () => {
      Message.success('补单成功')
      qc.invalidateQueries({ queryKey: ['admin-topups'] })
    },
    onError: (err: Error) => Message.error(err.message || '补单失败'),
  })

  const handleSearch = () => {
    setKeyword(searchInput.trim())
    setPage(1)
  }

  const handleComplete = (tradeNo: string) => {
    Modal.confirm({
      title: '确认补单',
      content: '是否将该订单标记为成功并为用户入账？',
      onOk: () => completeMut.mutateAsync(tradeNo),
    })
  }

  const columns = [
    {
      title: '订单号',
      dataIndex: 'trade_no',
      width: 220,
      render: (v: string) => <span className="font-mono text-xs break-all">{v}</span>,
    },
    { title: '用户 ID', dataIndex: 'user_id', width: 90 },
    {
      title: '支付方式',
      dataIndex: 'payment_method',
      width: 100,
      render: (v: string) => getMethodLabel(v, t) || v || '-',
    },
    {
      title: '充值额度',
      dataIndex: 'amount',
      width: 130,
      render: (v: number) => renderQuota(displayAmountToQuota(v)),
    },
    {
      title: '实付金额',
      dataIndex: 'money',
      width: 110,
      render: (v: number) => `¥${Number(v || 0).toFixed(2)}`,
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (v: string) => (
        <Pill variant={STATUS_VARIANT[v] || 'neutral'}>{STATUS_TEXT[v] || v}</Pill>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'create_time',
      width: 170,
      render: (v: number) => renderTimestamp(v),
    },
    {
      title: '完成时间',
      dataIndex: 'complete_time',
      width: 170,
      render: (v: number) => (v ? renderTimestamp(v) : '-'),
    },
    {
      title: '操作',
      width: 100,
      fixed: 'right' as const,
      render: (_: unknown, record: TopUpRecord) =>
        record.status === 'pending' ? (
          <Button size="mini" type="outline" onClick={() => handleComplete(record.trade_no)}>
            补单
          </Button>
        ) : null,
    },
  ]

  const items = list.data?.items || []
  const total = list.data?.total || 0

  return (
    <div className="space-y-4">
      <PageHeader
        title="充值订单"
        subtitle="全站在线充值与兑换订单；待支付订单可手动补单"
        actions={
          <Button icon={<IconRefresh />} onClick={() => list.refetch()} loading={list.isFetching}>
            刷新
          </Button>
        }
      />

      <Card className="!rounded-2xl">
        <div className="flex flex-col sm:flex-row gap-2 mb-4">
          <Input
            prefix={<IconSearch />}
            placeholder="搜索订单号"
            value={searchInput}
            onChange={setSearchInput}
            onPressEnter={handleSearch}
            allowClear
            className="w-full sm:w-[320px]"
          />
          <Button type="primary" onClick={handleSearch}>
            查询
          </Button>
        </div>

        <Table
          rowKey="id"
          loading={list.isLoading}
          columns={columns}
          data={items}
          scroll={{ x: 1200 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showTotal: true,
            sizeCanChange: true,
            onChange: (p, ps) => {
              setPage(p)
              setPageSize(ps)
            },
          }}
          noDataElement={<Empty description="暂无充值订单" />}
        />
      </Card>
    </div>
  )
}
